/**
 * Structure — TanStack Query hooks поверх /companies + /clusters + /farms
 * + /farms/bbox (агрегаты) и /fields/{id}/transfers.
 *
 * Backend: companies.py, clusters.py, farms.py, fields_detail.py.
 * Дерево склеивается на клиенте (buildStructureTree в structureLogic).
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ClusterIn,
  ClusterOut,
  CompanyOut,
  FarmBboxRow,
  FarmIn,
  FarmOut,
  FarmWithAggregate,
  TransferIn,
  TransferOut,
} from '../types';

export const structureKeys = {
  companies: () => ['structure', 'companies'] as const,
  clusters: () => ['structure', 'clusters'] as const,
  /** includeArchived входит в ключ: активные и архивные — разные наборы. */
  farms: (includeArchived = false) =>
    ['structure', 'farms', { includeArchived }] as const,
  farmsBbox: () => ['structure', 'farms-bbox'] as const,
  transfers: (fieldId: string) => ['structure', 'transfers', fieldId] as const,
};

export function useCompanies(): UseQueryResult<CompanyOut[], Error> {
  return useQuery({
    queryKey: structureKeys.companies(),
    queryFn: () => api.get<CompanyOut[]>('/api/v1/companies'),
    staleTime: 5 * 60_000,
  });
}

export function useClusters(): UseQueryResult<ClusterOut[], Error> {
  return useQuery({
    queryKey: structureKeys.clusters(),
    queryFn: () => api.get<ClusterOut[]>('/api/v1/clusters'),
    staleTime: 60_000,
  });
}

export function useFarms(
  includeArchived = false,
): UseQueryResult<FarmOut[], Error> {
  return useQuery({
    queryKey: structureKeys.farms(includeArchived),
    queryFn: () =>
      api.get<FarmOut[]>(
        `/api/v1/farms${includeArchived ? '?include_archived=true' : ''}`,
      ),
    staleTime: 60_000,
  });
}

/** Агрегаты полей/га по хозяйствам (только активные, archived исключены). */
export function useFarmsBbox(): UseQueryResult<FarmBboxRow[], Error> {
  return useQuery({
    queryKey: structureKeys.farmsBbox(),
    queryFn: () => api.get<FarmBboxRow[]>('/api/v1/farms/bbox'),
    staleTime: 60_000,
  });
}

/**
 * Единый источник дерева: склеивает /farms + /farms/bbox в FarmWithAggregate.
 * Возвращает companies, clusters, farms(+aggregate) и общий loading/error.
 *
 * `includeArchived` (тумблер «Показать архивные») подмешивает архивные
 * хозяйства через ?include_archived=true. Флаг `archived` маппится из
 * `archived_at` (бэк отдаёт его только при include_archived). /farms/bbox
 * агрегирует только активные — у архивных хозяйств field_count/area_ha = 0,
 * это ок (архивные показываем приглушённо, счётчики не важны).
 */
export function useStructureData(includeArchived = false) {
  const results = useQueries({
    queries: [
      {
        queryKey: structureKeys.companies(),
        queryFn: () => api.get<CompanyOut[]>('/api/v1/companies'),
        staleTime: 5 * 60_000,
      },
      {
        queryKey: structureKeys.clusters(),
        queryFn: () => api.get<ClusterOut[]>('/api/v1/clusters'),
        staleTime: 60_000,
      },
      {
        queryKey: structureKeys.farms(includeArchived),
        queryFn: () =>
          api.get<FarmOut[]>(
            `/api/v1/farms${includeArchived ? '?include_archived=true' : ''}`,
          ),
        staleTime: 60_000,
      },
      {
        queryKey: structureKeys.farmsBbox(),
        queryFn: () => api.get<FarmBboxRow[]>('/api/v1/farms/bbox'),
        staleTime: 60_000,
      },
    ],
  });

  const [companiesQ, clustersQ, farmsQ, bboxQ] = results;

  const companies = (companiesQ.data as CompanyOut[] | undefined) ?? [];
  const clusters = (clustersQ.data as ClusterOut[] | undefined) ?? [];
  const farms = (farmsQ.data as FarmOut[] | undefined) ?? [];
  const bbox = (bboxQ.data as FarmBboxRow[] | undefined) ?? [];

  const bboxById = new Map(bbox.map((b) => [b.id, b]));
  const farmsWithAggregate: FarmWithAggregate[] = farms.map((f) => {
    const agg = bboxById.get(f.id);
    return {
      ...f,
      field_count: agg?.field_count ?? 0,
      area_ha: agg?.total_area_ha ?? 0,
      archived: f.archived_at != null,
    };
  });

  return {
    companies,
    clusters,
    farms: farmsWithAggregate,
    isLoading: results.some((r) => r.isLoading),
    // bbox — вспомогательный (агрегаты); его ошибка не должна валить дерево.
    error: companiesQ.error ?? clustersQ.error ?? farmsQ.error ?? null,
    companyId: companies[0]?.id ?? null,
  };
}

/**
 * Префикс для инвалидации всех вариантов /farms (активные И архивные).
 * structureKeys.farms(bool) кладёт includeArchived в ключ, поэтому точечный
 * ключ инвалидировал бы лишь один набор — берём общий префикс.
 */
const FARMS_PREFIX = ['structure', 'farms'] as const;

// ─── Мутации: кластеры ──────────────────────────────────────────────────────

export function useCreateCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClusterIn) =>
      api.post<ClusterOut>('/api/v1/clusters', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: structureKeys.clusters() });
    },
  });
}

// ─── Мутации: хозяйства ─────────────────────────────────────────────────────

export function useCreateFarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FarmIn) => api.post<FarmOut>('/api/v1/farms', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FARMS_PREFIX });
      void qc.invalidateQueries({ queryKey: structureKeys.farmsBbox() });
    },
  });
}

export function useUpdateFarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ farmId, payload }: { farmId: string; payload: FarmIn }) =>
      api.patch<FarmOut>(`/api/v1/farms/${farmId}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FARMS_PREFIX });
      void qc.invalidateQueries({ queryKey: structureKeys.farmsBbox() });
    },
  });
}

export function useArchiveFarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (farmId: string) => api.delete<void>(`/api/v1/farms/${farmId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FARMS_PREFIX });
      void qc.invalidateQueries({ queryKey: structureKeys.farmsBbox() });
    },
  });
}

/**
 * Восстановление хозяйства из архива — POST /farms/{id}/restore (#393).
 * `restore_fields` (default true на бэке) — поднять ли каскадно-архивированные
 * поля вместе с хозяйством. Инвалидируем оба набора /farms + /farms/bbox
 * (архивные счётчики снова активны).
 */
export function useRestoreFarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      farmId,
      restoreFields,
    }: {
      farmId: string;
      restoreFields: boolean;
    }) =>
      api.post<FarmOut>(`/api/v1/farms/${farmId}/restore`, {
        restore_fields: restoreFields,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FARMS_PREFIX });
      void qc.invalidateQueries({ queryKey: structureKeys.farmsBbox() });
    },
  });
}

/**
 * Восстановление поля из архива — POST /fields/{id}/restore (#393).
 * `restore_sowings` (default true на бэке) — поднять ли каскадно-архивированные
 * посевы. Инвалидируем список полей + карту-инит вызывающий чистит сам.
 */
export function useRestoreField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      restoreSowings = true,
    }: {
      fieldId: string;
      restoreSowings?: boolean;
    }) =>
      api.post(`/api/v1/fields/${fieldId}/restore`, {
        restore_sowings: restoreSowings,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fields'] });
    },
  });
}

// ─── Передача поля ──────────────────────────────────────────────────────────

export function useFieldTransfers(
  fieldId: string,
  enabled = true,
): UseQueryResult<TransferOut[], Error> {
  return useQuery({
    queryKey: structureKeys.transfers(fieldId),
    queryFn: () => api.get<TransferOut[]>(`/api/v1/fields/${fieldId}/transfers`),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * Переименование поля — PATCH /fields/{id}. Бэк требует полный FieldIn
 * (farm_id, name обязательны), поэтому передаём существующие значения + новое
 * имя. «Действует с» / история имён (event sourcing) — за capability-флагом
 * (AK-344), пока пишем просто новое имя.
 */
export function useRenameField(fieldId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(`/api/v1/fields/${fieldId}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fields'] });
    },
  });
}

export function useCreateFieldTransfer(fieldId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TransferIn) =>
      api.post<TransferOut>(`/api/v1/fields/${fieldId}/transfers`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: structureKeys.transfers(fieldId) });
    },
  });
}
